import { storedAiLevelAtom } from "@/atoms/aiLevelAtom";
import { userAtom } from "@/atoms/userAtom";
import { Button } from "@/components/ui/button";
import { useAiLevel } from "@/hooks/useAiLevel";
import { cn } from "@/lib/utils";
import { useAtom, useAtomValue } from "jotai";
import { Info } from "lucide-react";
import { useEffect, useState } from "react";

type AiLevelDisplayProps = {
  /** インフォメーションアイコン押下時のハンドラ（オンボーディングツアーの再表示） */
  onInfoClick?: () => void;
};

/**
 * 提案画面上部に常時表示する AIレベル表示。
 *
 * - マウント時（= 提案画面への遷移時）に最新レベルを取得
 * - ローカル保存値（userId 一致時）と比較し、レベルアップしていれば
 *   「ゲージが 10/10 まで満ちる → バーが光る → 新レベルへ」の遷移演出を行う
 * - 取得した最新レベルを保存
 */
export const AiLevelDisplay = ({ onInfoClick }: AiLevelDisplayProps = {}) => {
  const user = useAtomValue(userAtom);
  const { level, gauge, refresh } = useAiLevel();
  const [stored, setStored] = useAtom(storedAiLevelAtom);

  // レベルアップ演出中の表示オーバーライド（null のときは取得値をそのまま表示）
  const [anim, setAnim] = useState<{ level: number; gauge: number } | null>(null);
  // ゲージバーを光らせるフラグ
  const [barGlow, setBarGlow] = useState(false);

  useEffect(() => {
    const userId = user?.userId;
    if (!userId) return;
    let cancelled = false;
    const timers: ReturnType<typeof setTimeout>[] = [];
    (async () => {
      const data = await refresh();
      if (cancelled || !data) return;
      const prevLevel = stored && stored.userId === userId ? stored.level : null;
      // 初回（prevLevel=null）は演出なし。レベルアップ時のみ遷移演出。
      if (prevLevel != null && data.level > prevLevel) {
        // 1. 現レベルのままゲージを 10/10 まで満たす
        setAnim({ level: prevLevel, gauge: 10 });
        // 2. 満ちたらバーを光らせる
        timers.push(
          setTimeout(() => {
            if (!cancelled) setBarGlow(true);
          }, 700)
        );
        // 3. 光り終えたら新レベルへ（anim 解除で取得値=新レベルに戻る）
        //    演出を見せ終えてから「確認済み」として保存する（未視聴のまま消費しない）。
        //    途中で離脱・リロードした場合は保存されず、次回提案画面で必ず再生される。
        timers.push(
          setTimeout(() => {
            if (cancelled) return;
            setBarGlow(false);
            setAnim(null);
            setStored({ userId, level: data.level });
          }, 1900)
        );
      } else {
        // レベルアップ無しは即時に同期（初回ユーザーのベースライン保存を含む）
        setStored({ userId, level: data.level });
      }
    })();
    return () => {
      cancelled = true;
      timers.forEach(clearTimeout);
    };
    // 提案画面遷移（マウント）時のみ実行する
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.userId]);

  if (!user?.userId) return null;

  const shownLevel = anim ? anim.level : level;
  const shownGauge = anim ? anim.gauge : gauge;
  const remaining = 10 - shownGauge;

  return (
    <div className="rounded-lg border border-border/20 bg-muted/30 p-3">
      <div className="flex items-center gap-1.5">
        <span className="text-sm font-bold">AI レベル {shownLevel}</span>
        <span className="text-sm text-muted-foreground">・スポットの好みを学習中...</span>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label="AIレベルの使い方を見る"
          className="h-6 w-6 shrink-0 text-muted-foreground"
          onClick={onInfoClick}
        >
          <Info className="h-4 w-4" />
        </Button>
      </div>
      <div className="mt-2">
        <div className="flex items-center gap-2">
          {/* ゲージ（自前実装）: shadcn Progress を改変しないための独立コンポーネント */}
          <div
            className={cn(
              "h-2 flex-1 overflow-hidden rounded-full bg-secondary",
              barGlow && "animate-glow"
            )}
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={10}
            aria-valuenow={shownGauge}
          >
            <div
              className="h-full rounded-full bg-gradient-to-r from-[#9CFB8F] to-[#8D9DFF] transition-[width] duration-500"
              style={{ width: `${shownGauge * 10}%` }}
            />
          </div>
          <span className="shrink-0 text-xs text-muted-foreground">{shownGauge} / 10</span>
        </div>
        <div className="mt-1 text-xs text-muted-foreground">
          あと{remaining}ptでレベルアップ（興味なし+1 / 行った+3 / 行きたい+5）
        </div>
      </div>
    </div>
  );
};
